import BaseChart from './BaseChart'
import ItemsHelper from '../chart_helpers/ItemsHelper'
import RootHelper from '../chart_helpers/RootHelper'
import SelectableHelper from '../chart_helpers/SelectableHelper'

// import { arc as d3_arc } from 'd3-shape'

import { treemap as d3_treemap, hierarchy as d3_hierarchy } from 'd3-hierarchy'
// import { voronoiTreemap as d3_voronoiTreemap } from 'd3-voronoi-treemap'
// import { voronoiMapSimulation as d3_voronoiMapSimulation } from 'd3-voronoi-map'
import * as d3_voronoi_map from '../lib/d3-voronoi-map/index.js'
import { path as d3_path } from 'd3-path'
import { interpolateRgb as d3_interpolateRgb } from 'd3-interpolate'
import { select as d3_select } from 'd3-selection'
import { line as d3_line } from 'd3-shape'
import * as d3_polygon from 'd3-polygon'
import { rgb as d3_rgb } from 'd3-color'
import SizeHelper from '../chart_helpers/SizeHelper'
import { ellipseSvgText, wrapSvgText } from '../utils/textUtils'
import { formatNumber } from '../utils/formatUtils'
import { getBrightness } from '../utils/colorUtils'
import { randomLcg as d3_randomLcg } from 'd3-random'
import { largestRect as d3_largestRect } from 'd3plus-shape'
import * as COLORS from '../utils/colorUtils'
import { isDarkMode } from '../utils/configUtils.js'
import { ACCESSIBILITY_OPTIONS_KEYS } from '../utils/accessibilityUtils.js'

function computeClippingPolygon(sides = 8, radius = 1, width = 1) {
  if (sides === Infinity || isNaN(sides) || sides > 12) sides = 50
  var circlingPolygon = []
  for (let a = 0; a < Math.PI * 2; a += (Math.PI * 2) / sides) {
    circlingPolygon.push([radius + (radius - 1) * Math.cos(a), radius + (radius - 1) * Math.sin(a)])
  }
  circlingPolygon.forEach(p => {
    p[0] += (width - 2 * radius) / 2
  })
  circlingPolygon.radius = radius
  circlingPolygon.width = width

  return circlingPolygon
}

export default class TreemapChart extends BaseChart {
  constructor(...args) {
    super(...args)
  }
  static defaultConfig = {
    mode: {
      type: [String],
      default: 'rect',
      options: ['rect', 'voronoi'],
      exposed: true,
    },
    dataSorting: {
      type: [String],
      default: 'none',
      options: ['none', 'asc', 'desc'],
      exposed: true,
    },
    labelValue: {
      type: [String],
      default: 'none',
      options: ['none', 'abs', 'percent'],
      exposed: true,
    },
    /**
     * {-EN-} If set, all values smaller than this threshold will be grouped in a single sector labeled "Others" by default.
     * {-ES-} Si se establece, todos los valores inferiores a este umbral se agruparán en un único sector denominado "Otros" por defecto.
     */
    groupOthersThreshold: {
      type: [Number, null],
      default: null,
      exposed: true,
    },
    /**
     * {-EN-} Customize the label of grouped values using the `groupOthersThreshold` prop.
     * {-ES-} Personaliza la etiqueta de los valores agrupados utilizando la propiedad groupOthersThreshold.
     */
    groupOthersLabel: {
      type: String,
      default: 'Others',
      exposed: true,
    },
    /**
     * {-EN-} If set to `true`, sector values will be show as a percentage of the total.
     * {-ES-} Si se establece en `true`, los valores del sector se mostrarán como un porcentaje del total.
     */
    showPercentage: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    /**
     * {-EN-} Format for the percentages.
     * {-ES-} Formato para los porcentajes.
     */
    percentageFormat: {
      type: [String],
      default: '[N]0,0.0',
      exposed: true,
    },
    /**
     * {-EN-} If set, all sectors will span at least this angle. Useful if some values are too small to be seen.
     * {-ES-} Si se establece, todos los sectores abarcarán al menos este ángulo. Útil si algunos valores son demasiado pequeños para verse.
     */
    minSectorAngle: {
      type: Number,
      default: 0,
      exposed: true,
    },

    /**
     * {-EN-} Show/hide labels for each sector.
     * {-ES-} Mostrar/ocultar etiquetas para cada sector.
     */
    showLabels: {
      type: Boolean,
      default: true,
      exposed: true,
    },

    labelWrap: {
      type: [String],
      default: 'ellipsis',
      options: ['wrap', 'ellipsis', 'none'],
      exposed: true,
    },

    /**
     * {-EN-} Show/hide values for each sector.
     * {-ES-} Mostrar/ocultar valores para cada sector.
     */
    showDatumValues: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    hasColorPatterns: {
      type: Boolean,
      default: true,
      exposed: false,
    },
    TooltipBehavior: {
      getHtml: function (d) {
        //Chapuzilla porque no esta en ningun axis, y no nos vale "formatValue(value,axis)"
        let formatter = (value, format) => {
          let match = this.matchFormatString(format || '')
          let numberFormat = match && match[2]
          let thouDel = this.config.thousandsDelimiter
          let decDel = this.config.decimalDelimiter
          return formatNumber(value, numberFormat, thouDel, decDel)
        }
        if (d.data.othersData) {
          let datas = d.data.othersData
          let html = datas.map(dd => {
            return `
            <div class="tip-value important">${this.formatTooltipValue(dd.value)}</div>
            <div class="tip-id">
              <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
                dd
              )}; margin-right: 3px"></span>
              ${dd.id}
            </div>
            ${
              this.config.showPercentage
                ? '<div class="secondary">' +
                  formatter(dd.percent, this.config.percentageFormat) +
                  '%</div>'
                : ''
            }
            `
          })
          return html.join('<br/>')
        } else {
          return `
          <div class="tip-value important">${this.formatTooltipValue(d.data.value)}</div>
          <div class="tip-id">
            <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
              d
            )}; margin-right: 3px"></span>
            ${d.id}
          </div>
          ${
            this.config.showPercentage
              ? '<div class="secondary">' +
                formatter(d.data.percent, this.config.percentageFormat) +
                '%</div>'
              : ''
          }
          `
        }
      },
      getItemOffset: function ($el, d) {
        let targetRect = $el.getBoundingClientRect()
        return {
          x: targetRect.x + targetRect.width / 2,
          y: targetRect.y + targetRect.height / 2,
        }
      },
    },
  }

  static helpers = [RootHelper, ItemsHelper, SelectableHelper, SizeHelper]
  formatter(value, format) {
    let match = this.matchFormatString(format || '')
    let numberFormat = match && match[2]
    let thouDel = this.config.thousandsDelimiter
    let decDel = this.config.decimalDelimiter
    return formatNumber(value, numberFormat, thouDel, decDel)
  }
  beforeHelpers() {
    // this.content = this.root.selectAll('.content').data([null])
    // this.content = this.content.merge(this.content.enter().append('g').attr('class','content'))
  }
  static simulation
  static drawnState
  draw() {
    function fillTween(d) {
      const fill = self.fill(d)
      if (fill?.includes('url'))
        return function (t) {
          return fill
        }
      else {
        const currentFill = d3_select(this).attr('fill')
        var i = d3_interpolateRgb(currentFill, fill)
        return function (t) {
          return i(t)
        }
      }
    }

    let states = []
    console.log('itemsEnter', this.itemsEnter.data())

    // let dataClone = JSON.parse(JSON.stringify(this.data))
    // let tree = d3_hierarchy({ id: 'root', children: this.data })
    // tree.children.forEach(d => (d.id = d.data.id))
    // Then d3.treemap computes the position of each element of the hierarchy
    let self = this

    const setLabelRects = () => {
      const isVoronoi = self.config.mode !== 'rect'
      //this.itemsEnter.select('.text-group').insert('rect', 'text')
      const itemsMerged = this.items.merge(this.itemsEnter)
      itemsMerged.each(function (d, i) {
        const fill = self.fill(d)
        const hasPattern = fill?.includes('url')
        const itemSelection = d3_select(this)
        const voronoiItemPath = itemSelection.select('path') // Assuming path is the Voronoi cell

        // Append clipPath to the SVG
        let clipPath = d3_select(self.$root)
          .select('defs')
          .selectAll(`#clip-${i}`)
          .data(hasPattern && isVoronoi ? [null] : [])

        const clipPathEnter = clipPath
          .enter()
          .append('clipPath')
          .attr('id', `clip-${i}`)
          .attr('clipPathUnits', 'userSpaceOnUse') // Create unique clip path ID

        clipPathEnter.append('path')

        clipPath.exit().remove()

        clipPath = clipPath.merge(clipPathEnter)

        clipPath.select('path').attr('d', voronoiItemPath.attr('d'))

        let displayText = true
        if (hasPattern) {
          itemSelection
            .select('.text-group')
            .selectAll('tspan')
            .each(function () {
              displayText = displayText && this.style.display !== 'none'
            })
        }

        itemSelection.attr('clip-path', `url(#clip-${i})`)

        let rect = itemSelection
          .select('.text-group')
          .selectAll('rect')
          .data(d => (hasPattern && displayText ? [null] : []))
        rect.exit().remove()
        const rectEnter = rect.enter().append('rect').style('opacity', 0.9)
        rect = rect.merge(rectEnter)
        rect.lower()

        const textNodeBCR = itemSelection
          .select('.text-group')
          .select('text')
          .node()
          ?.getBoundingClientRect()

        const rectWidth = textNodeBCR.width - 1
        const rectHeight = textNodeBCR.height - 1
        const rectX = isVoronoi ? (rectWidth / 2) * -1 : 0
        const rectY = isVoronoi ? (rectHeight / 4) * -1 : 0
        rect
          .attr('transform', `translate(${rectX}, ${rectY})`)
          .attr('width', rectWidth) // Add padding to the width: ;
          .attr('height', rectHeight) // Add padding to the height
          .attr('fill', self.color(d)) // Background color

        itemSelection.select('.text-group').transition(100).style('opacity', 1)
        //rect.transition(500).style('opacity', 1) // Background color
      })

      //itemsMerged.select('.text-group').transition(200).style('opacity', 1)
    }

    const hideTextGroups = () => {
      this.items.select('.text-group').style('opacity', '0')
      this.items.attr('clip-path', undefined)
    }

    if (this.config.mode === 'rect') {
      d3_treemap().size([this.size.width, this.size.height]).padding(8).paddingInner(2)(
        this.data.__root
      )
      // d3_voronoiTreemap().clip(
      //   computeClippingPolygon(this.config.voronoiSides, this.size.height / 2, this.size.width)
      // )(tree)
    } else {
      this.items.merge(this.itemsEnter).each(function (d) {
        d.previousWeight = this._previousWeight || d.value
        d.previousX = this._previousX || Math.random()
        d.previousY = this._previousY || Math.random()
      })
      let children = this.items.merge(this.itemsEnter).data()
      let shouldSkip
      if (!this.simulation) {
        this.simulation = d3_voronoi_map.voronoiMapSimulation(children)
        console.log('LOOP')
      } else if (
        // !children.length ||
        children.length &&
        this.drawnState
      ) {
        let oldItems = this.drawnState.polygons.map(
          p => p.site.originalObject.data.originalData.data
        )
        let oldVsNew = oldItems.every(n => children.find(d => d.id === n.id)?.value === n.value)
        let newVsOld = children.every(n => oldItems.find(d => d.id === n.id)?.value === n.value)
        if (oldVsNew && newVsOld) {
          // console.log('will skip because same values!',oldVsNew,newVsOld)
          shouldSkip = true
        }
      }
      let width = this.size.width
      let radius = this.size.height / 2
      let clipPoly = computeClippingPolygon(this.config.voronoiSides, radius, width)
      if (
        this.simulation.clip().length !== clipPoly.length ||
        this.simulation.clip().radius !== radius ||
        this.simulation.clip().width !== width
      ) {
        shouldSkip = false
      }
      var state
      if (!shouldSkip && children.length > 0) {
        //Hay q quitar los clip-paths de los labels para que no rompa la simulacion
        hideTextGroups()
        // console.log('will simulate')
        this.simulation.stop()

        this.simulation = d3_voronoi_map
          .voronoiMapSimulation(children)
          .prng(d3_randomLcg('svd-seed'))
          .weight(d => d.value) // set the weight accessor
          .initialPosition(d => [d.previousX, d.previousY])
          .initialWeight(d => d.previousWeight)
          .maxIterationCount(500)
          .clip(clipPoly)
          .on('end', () =>
            setTimeout(
              setLabelRects,
              this.config.ItemsHelper.transition === true
                ? 1000
                : this.config.ItemsHelper.transition
            )
          )

        state = this.simulation.state()
        states.push(state) // retrieve the simulation's state, i.e. {ended, polygons, iterationCount, convergenceRatio}
        while (!state.ended) {
          // manually launch each iteration until the simulation ends
          this.simulation.tick()
          state = this.simulation.state()
          states.push(state)
        }
      } else {
        // console.log('will not simulate')
        state = this.simulation.state()
        setLabelRects()
      }
      // var myseededprng = new Math.seedrandom('sdv')
      children.forEach((d, i) => {
        // state.polygons.map(p => p.site.originalObject.data.originalData.data.value)
        d.polygon = state.polygons.find(
          p => p.site.originalObject.data.originalData.data.id === d.id
        )
      })

      // states.forEach(s => {
      //   s.polygons.sort((a, b) => {
      //     return b.site.originalObject.index - a.site.originalObject.index
      //   })
      // })
      this.items.each(function (d) {
        this._previousWeight = d.value
        this._previousX = d.polygon.site.x
        this._previousY = d.polygon.site.y
      })
    }
    // use this information to add rectangles:
    // svg
    //   .selectAll("rect")
    //   .data(root.leaves())
    //   .enter()

    let line = points => {
      // var offset = new Offset().arcSegments(2)
      // var margined = offset
      //   .data(points)
      //   .padding(2)
      //   .map(p => p.reverse())[0]
      return d3_line()(points) + 'z'
    }

    this.itemsEnter
      .filter(d => !d.data.children || d.data.children?.length === 0)
      .append('path')
      // .attr('x', d => d.x0)
      // .attr('y', d => d.y0)
      // .attr('width', d => d.x1 - d.x0)
      // .attr('height', d => d.y1 - d.y0)
      .attr('d', function (d) {
        if (self.config.mode === 'rect') {
          let p = d3_path()
          p.rect(d.x0, d.y0, d.x1 - d.x0, d.y1 - d.y0)
          return p.toString()
        } else return line(d.polygon) // d.polygon is the computed Voronoï cell encoding the relative weight of your underlying original data
      })
      .style('opacity', 0)
      .style('display', d => (d.depth === 0 ? 'none' : 'inherit'))

    this.itemsEnter
      .filter(d => !d.data.children || d.data.children?.length === 0)
      .append('g')
      .attr('class', 'text-group')
      .style('opacity', 0)
      .attr('transform', d => {
        if (self.config.mode === 'voronoi') {
          let centroid = d3_polygon.polygonCentroid(d.polygon)
          return `translate(${centroid[0]},${centroid[1]})`
        } else {
          return `translate(${d.x0 + 10},${d.y0 + 10})`
        }
      })
      .append('text')
      .attr('dominant-baseline', this.config.mode === 'voronoi' ? 'ideographic' : 'middle')
      .attr('fill', d => {
        let brightness = getBrightness(this.color(d))
        return brightness < 140 ? '#fff' : '#000'
      })
      .attr('text-anchor', () => (this.config.mode === 'voronoi' ? 'middle' : 'start'))

    this.items
      .merge(this.itemsEnter)
      .select('.text-group')
      .select('text')
      .attr(
        'font-size',
        this._parent.accessibilityOptions?.[ACCESSIBILITY_OPTIONS_KEYS.increaseTextSize]
          ? '18px'
          : '14px'
      )
      .html(
        d =>
          (this.config.showLabels
            ? (d.data.parentLabel
                ? `<tspan font-weight="600" x="0" dy=".6em">${d.data.parentLabel}</tspan>`
                : '') +
              `<tspan font-weight="${d.data.parentLabel ? 400 : 600}" x="0" dy="${
                d.data.parentLabel ? '1.4em' : '.6em'
              }">${d.data.label}</tspan>`
            : '') +
          `<tspan x="0" dy="1.2em">${
            this.config.showDatumValues ? this.valueWithUnit(d.data.value) : ''
          }</tspan>`
      )
      .each(function (d) {
        const MIN_MAX_WIDTH = 20
        const MIN_MAX_HEIGHT = 20
        let container
        if (self.config.mode === 'rect') {
          container = { width: d.x1 - d.x0 - 20, height: d.y1 - d.y0 - 20 }
        } else {
          let text_dimensions = this.getBoundingClientRect()
          const ratio = text_dimensions.width / text_dimensions.height
          const rect = d3_largestRect(d.polygon, { angle: 0, aspectRatio: ratio }).points
          container = { width: rect[1][0] - rect[0][0] - 10, height: rect[2][1] - rect[0][1] }
        }
        if (self.config.labelWrap !== 'none') {
          if (container.width > MIN_MAX_WIDTH && container.height > MIN_MAX_HEIGHT) {
            if (self.config.labelWrap === 'ellipsis') {
              d3_select(this)
                .selectAll('tspan')
                .style('display', 'inherit')
                .call(ellipseSvgText, Math.max(0, container.width))
            } else {
              d3_select(this)
                .selectAll('tspan')
                .style('display', 'inherit')
                .call(wrapSvgText, Math.max(0, container.width))
            }
          } else {
            d3_select(this).selectAll('tspan').style('display', 'none')
          }
        }
      })

    this.itemsEnterTrans
      .select('path')
      .style('opacity', 1)
      //.attr('fill', d => this.color(d))
      .attr(
        'stroke',
        !isDarkMode(this.config, this._parent.accessibilityOptions)
          ? COLORS.SKIN_BACKGROUND
          : COLORS.SKIN_BACKGROUND_DARK
      )
      .attr('stroke-width', this.config.mode === 'voronoi' ? '2' : '0')
      .attrTween('fill', fillTween)

    this.itemsEnterTrans.select('.text-group').style('opacity', 1)

    this.itemsTrans
      .select('path')
      //.attr('fill', d => this.color(d))
      .style('opacity', 1)
      .attr(
        'stroke',
        !isDarkMode(this.config, this._parent.accessibilityOptions)
          ? COLORS.SKIN_BACKGROUND
          : COLORS.SKIN_BACKGROUND_DARK
      )
      .attr('stroke-width', this.config.mode === 'voronoi' ? '2' : '0')
      .attrTween('fill', fillTween)

    if (self.config.mode === 'rect') {
      this.itemsTrans.select('path').attr('d', d => {
        let p = d3_path()
        p.rect(d.x0, d.y0, d.x1 - d.x0, d.y1 - d.y0)
        return p.toString()
      })
    } else {
      this.itemsTrans.select('path').attrTween('d', (d, i) => {
        return t => {
          let curr =
            states[Math.floor(t * (states.length - 1))] || states[states.length - 1] || state
          this.drawnState = curr
          // console.log('attrtween idx', t, states.length, Math.floor(t * (states.length - 1)))
          // (states[Math.floor(t * 300)] && states[Math.floor(t * 300)]) ||
          // states[states.length - 1]
          // console.log('curr', states.indexOf(curr))
          let polygon = curr.polygons.find(
            p => p.site.originalObject.data.originalData.data.id === d.id
          )
          return line(polygon)
        }
      })
    }

    this.itemsTrans
      .select('.text-group')
      //.style('opacity', 1)
      .select('text')
      .attr('fill', d => {
        let brightness = getBrightness(this.color(d))
        return brightness < 140 ? '#fff' : '#000'
      })

    if (this.config.mode === 'voronoi') {
      this.itemsTrans.select('.text-group').attrTween('transform', d => {
        return t => {
          let curr =
            states[Math.floor(t * (states.length - 1))] || states[states.length - 1] || state
          let polygon = curr.polygons.find(
            p => p.site.originalObject.data.originalData.data.id === d.id
          )
          let centroid = d3_polygon.polygonCentroid(polygon)
          return `translate(${centroid[0]},${centroid[1]})`
        }
      })
    } else {
      this.itemsTrans.select('.text-group').attr('transform', d => {
        return `translate(${d.x0 + 10},${d.y0 + 10})`
      })
    }

    this.itemsExitTrans.style('opacity', 0)

    if (this.config.mode === 'rect') {
      setLabelRects()
    }

    // we must reset selection (NO ME GUSTA, lo quito)
    // this.selection = this.data.filter(d => this.selection.find(dd => dd.id === d.id) !== undefined)
    // this._updateSelection()
  }
  setSelectedFunc(sel, d) {
    // let { radius, innerRadius } = this.getRadiuses()
    // // let offset = (radius - innerRadius)*0.75
    // let offset
    // if (this.config.donutThickness === 'thin') offset = (radius - innerRadius) * 0.75
    // else if (this.config.donutThickness === 'medium') offset = (radius - innerRadius) * 0.15
    // else offset = (radius - innerRadius) * 0.1
    // radius = radius + offset
    // innerRadius = innerRadius - offset
    // let arc = d3_arc().innerRadius(innerRadius).outerRadius(radius)
    // // let glow = sel.selectAll('.glow').data(this.config.donutThickness==='thin'?[d]:[])
    // let glow = sel.selectAll('.glow').data([d])
    // glow.exit().remove()
    // glow = glow.merge(glow.enter().append('path').attr('class', 'glow'))
    // glow
    //   .attr('d', arc)
    //   .attr('fill', this.color(d))
    //   .attr('opacity', 0)
    //   .transition()
    //   .duration(200)
    //   .attr('opacity', 0.25)
  }
  unsetSelectedFunc(sel) {
    // if (this.config.mode !== 'donut') return
    // let glow = sel.selectAll('.glow').data([])
    // glow.exit().remove()
  }
}
